import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { articlesMeta } from '@/mocks/articlesData';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setGuideOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <span className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 tracking-tight">
                Sugargoo<span className="text-primary-500">Sheet</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  isActive('/') ? 'text-primary-500' : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                Home
              </Link>

              <div className="relative" ref={guideRef}>
                <button
                  onClick={() => setGuideOpen(!guideOpen)}
                  className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-1 cursor-pointer ${
                    guideOpen || articlesMeta.some((a) => isActive(`/${a.slug}`))
                      ? 'text-primary-500'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  Shopping Guide
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {guideOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-2xl border border-background-200/60 shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
                    {articlesMeta.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/${article.slug}`}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                      >
                        <span className="text-xs px-1.5 py-0.5 bg-primary-100 text-primary-600 rounded">
                          {article.category}
                        </span>
                        <span className="flex-1 truncate">{article.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Visit Main Site
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>

            {/* Mobile Hamburger */}
            <div className="md:hidden flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 text-background-50 text-xs font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Main Site
                <i className="ri-arrow-right-up-line w-3 h-3 flex items-center justify-center"></i>
              </a>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} w-5 h-5 flex items-center justify-center`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 right-0 w-80 max-w-[85vw] h-[calc(100vh-4rem)] bg-background-50 border-l border-background-200/60 overflow-y-auto">
            <div className="p-5 space-y-6">
              {/* Main Nav */}
              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Navigation</h4>
                <div className="space-y-1">
                  <Link
                    to="/"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-home-4-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                    Home
                  </Link>
                </div>
              </div>

              {/* Articles */}
              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Shopping Guide</h4>
                <div className="space-y-1">
                  {articlesMeta.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/${article.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <span className="text-[10px] px-1.5 py-0.5 bg-primary-100 text-primary-600 rounded font-medium shrink-0">
                        {article.category}
                      </span>
                      <span className="truncate">{article.title}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Product Categories</h4>
                <div className="space-y-1">
                  {categories.slice(0, 8).map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-accent-500`}></i>
                      <span className="truncate">{cat.name}</span>
                      <i className="ri-arrow-right-up-line w-3 h-3 flex items-center justify-center text-foreground-400 ml-auto shrink-0"></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="pt-4 border-t border-background-200/60">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-xl hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  Visit Main Website
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}